import { z } from "zod";

import { QuestionType, SaleTypeForTerm } from "@/lib/gql/__generated__/graphql";
import {
	BasicCustomMailOptionalItemOverrideInputSchema,
	BasicCustomMailRequiredItemOverrideInputSchema,
	EmailTemplateReservationInputSchema,
	LongStayParamsInputSchema,
	PlanInputSchema,
	PriceRankRoomItemInputSchema,
	QuestionItemInputSchema,
	RoomPriceObjectInputSchema,
	ShortStayParamsInputSchema,
	StaticRoomPriceParamsInputSchema,
} from "@/lib/zod/__generated__/validation-schema";

import {
	chillnnDateZodSchema,
	dayOfTheWeekZodSchema,
	includedMealsZodSchema,
	kidsPolicyZodSchema,
	priceObjectZodSchema,
	requiredPrepMilliSecondBeforeDateZodSchema,
	requiredPrepMilliSecondBeforeHourZodSchema,
	s3ObjectZodSchema,
} from "@/components/features/_shared/validation-schema/validation-schema";

import type { ZodError } from "zod";

export const planInputZodSchema = PlanInputSchema.extend({
	name: z.string().min(1).max(30),
	mainTarget: z.string().max(30).nullish(),
	mainKeyVisual: s3ObjectZodSchema,
	keyVisuals: z.array(s3ObjectZodSchema),

	price: priceObjectZodSchema,

	kidsPolicy: kidsPolicyZodSchema.nullish(),
	coSleepingKidsPrice: priceObjectZodSchema.nullish(),

	requiredPrepMillisecondBeforeDate: requiredPrepMilliSecondBeforeDateZodSchema,
	requiredPrepMillisecondBeforeHour: requiredPrepMilliSecondBeforeHourZodSchema,

	maxPeopleNumPerRoom: z.number().min(0).max(100).nullish(),
	minimumPeopleNumPerRoom: z.number().min(1).max(100),

	minRoomNum: z.number().min(1).max(30),
	maxRoomNum: z.number().min(1).max(100).nullish(),

	checkInStart: z.string().nullish(),
	checkInEnd: z.string().nullish(),
	checkOut: z.string().nullish(),

	availableRoomIDs: z
		.array(z.string())
		// 選択可能な部屋
		.refine(
			(availableRoomIDs) => {
				if (!availableRoomIDs || availableRoomIDs.length <= 0) {
					return false;
				}

				return true;
			},
			{
				message: "予約可能な部屋は1つ以上選択してください",
				path: ["value"],
			},
		),

	checkInAvailableDayOfTheWeek: dayOfTheWeekZodSchema
		// チェックイン可能な曜日
		.refine(
			(checkInAvailableDayOfTheWeek) => {
				return checkInAvailableDayOfTheWeek.length > 0;
			},
			{
				message: "チェックイン可能な曜日は１つ以上選択してください",
				path: ["value"],
			},
		),
	stayableDayOfTheWeek: dayOfTheWeekZodSchema,
	isHolidayStayable: z.boolean().nullish(),
	isHolidayCheckInAvailable: z.boolean().nullish(),

	includedMeals: includedMealsZodSchema.nullish(),

	saleStart: chillnnDateZodSchema.nullish().or(z.literal("")),
	saleEnd: chillnnDateZodSchema.nullish().or(z.literal("")),

	stayableStart: chillnnDateZodSchema.nullish(),
	stayableEnd: chillnnDateZodSchema.nullish(),

	saleStartBeforeDate: z.number().int().min(1).max(1_000, { message: "1000日以内に設定してください" }).nullish(),

	maxDailyStockNum: z.number().int().min(1).max(100_000).nullish(),

	maxStockNum: z.number().int().min(1).max(100_000).nullish(),

	emailTemplateReservation: EmailTemplateReservationInputSchema.extend({
		atCreated: z.string().max(3_000).nullish(),
		atUpdated: z.string().max(3_000).nullish(),
		atCanceled: z.string().max(3_000).nullish(),
		atCin: z.string().max(3_000).nullish(),
	}).nullish(),

	longStayParams: LongStayParamsInputSchema.extend({
		minimumStayDates: z.number().min(1).max(365),
	}).nullish(),

	shortStayParams: ShortStayParamsInputSchema.extend({
		maximumStayDates: z.number().min(1).max(365),
	}).nullish(),

	questions: z.array(
		QuestionItemInputSchema.extend({
			question: z.string().min(1).max(8_000),
			options: z.array(z.string().max(500)).nullish(),
			required: z.boolean().nullish(),
		}).refine(
			({ questionType, options }) => {
				if (questionType === QuestionType.Free) {
					return true;
				}

				return typeof options === "object" && options?.every((option) => option.length > 0);
			},
			{
				message: "選択肢を入力してください",
				path: ["questionType"],
			},
		),
	),

	staticPriceParams: StaticRoomPriceParamsInputSchema.extend({
		staticRoomPrices: z.array(
			z.lazy(() =>
				PriceRankRoomItemInputSchema.extend({
					prices: z
						.array(
							z.lazy(() =>
								RoomPriceObjectInputSchema.extend({
									adultNum: z.number(),
									price: priceObjectZodSchema.nullish(),
								}),
							),
						)
						.nullish(),
					roomID: z.string(),
					roomName: z.string(),
				}),
			),
		),
	}).nullish(),

	// カスタムメール設定

	atCreated: BasicCustomMailRequiredItemOverrideInputSchema.extend({
		text: z.string().max(3_000),
		override: z.boolean(),
	}),
	atUpdated: BasicCustomMailRequiredItemOverrideInputSchema.extend({
		text: z.string().max(3_000),
		override: z.boolean(),
	}),
	atCanceled: BasicCustomMailRequiredItemOverrideInputSchema.extend({
		text: z.string().max(3_000),
		override: z.boolean(),
	}),
	atCheckInBefore_07days: BasicCustomMailOptionalItemOverrideInputSchema.extend({
		text: z.string().max(3_000),
		shouldSendEmail: z.boolean(),
		override: z.boolean(),
	}),
	atCheckInBefore_01days: BasicCustomMailOptionalItemOverrideInputSchema.extend({
		text: z.string().max(3_000),
		shouldSendEmail: z.boolean(),
		override: z.boolean(),
	}),
	atCheckInBefore_00days: BasicCustomMailOptionalItemOverrideInputSchema.extend({
		text: z.string().max(3_000),
		shouldSendEmail: z.boolean(),
		override: z.boolean(),
	}),
	atCheckOutAfter_01days: BasicCustomMailOptionalItemOverrideInputSchema.extend({
		text: z.string().max(3_000),
		shouldSendEmail: z.boolean(),
		override: z.boolean(),
	}),
}) // ↓ 複数の値によるバリデーションを設定するために、refineを使用している
	// １部屋につき宿泊可能な人数
	.refine(
		({ maxPeopleNumPerRoom, minimumPeopleNumPerRoom }) => {
			if (typeof maxPeopleNumPerRoom === "number" && maxPeopleNumPerRoom < minimumPeopleNumPerRoom) {
				return false;
			}

			return true;
		},
		{
			message: "最大人数は最小人数以上になるように設定してください",
			path: ["maxPeopleNumPerRoom"],
		},
	)
	// １度の予約につき宿泊可能な部屋
	.refine(
		({ maxRoomNum, minRoomNum }) => {
			if (typeof maxRoomNum === "number" && maxRoomNum < minRoomNum) {
				return false;
			}

			return true;
		},
		{
			message: "最高部屋数は最低部屋数以上になるように設定してください",
			path: ["maxRoomNum"],
		},
	)
	// 添い寝の子供の設定
	.refine(
		({ coSleepingKidsPrice, isCoSleepingKidsPriceActive }) => {
			if (!isCoSleepingKidsPriceActive) {
				return true;
			}

			return typeof coSleepingKidsPrice?.amount === "number";
		},
		{
			message: "添い寝の子供の料金は必須項目です",
			path: ["coSleepingKidsPrice", "amount"],
		},
	)
	// 公開する期間
	.refine(
		({ saleStart, saleEnd, saleStartTime, saleEndTime, saleTypeForTerm }) => {
			if (saleTypeForTerm === SaleTypeForTerm.Limited) {
				if (!!saleStart && !!saleEnd) {
					// 同じ日付の場合は時刻もチェック
					if (saleStart === saleEnd && !!saleStartTime && !!saleEndTime) {
						return saleStartTime < saleEndTime;
					}
					// 異なる日付の場合は日付のみをチェック
					return saleStart <= saleEnd;
				}

				return true;
			}

			return true;
		},
		{
			message: "終了日時が開始日時以降になるように設定してください",
			path: ["saleEnd"],
		},
	)
	// 滞在可能な期間
	.refine(
		({ stayableStart, stayableEnd, saleTypeForTerm }) => {
			if (saleTypeForTerm === SaleTypeForTerm.Limited) {
				if (!!stayableStart && !!stayableEnd) {
					return stayableStart <= stayableEnd;
				}

				return !!stayableStart;
			}

			return true;
		},
		{
			message: "滞在可能な期間を設定してください",
			path: ["stayableStart"],
		},
	)
	// チェックイン時間
	.refine(
		({ checkInStart, checkInEnd }) => {
			if (!checkInStart || !checkInEnd) {
				return true;
			}

			return checkInStart <= checkInEnd;
		},
		{
			message: "チェックインの終了時間は開始時間と同じか、それよりも後になるように設定してください",
			path: ["checkInEnd"],
		},
	)
	// 最長連泊期間
	.refine(
		({ shortStayParams, longStayParams }) => {
			if (!longStayParams) {
				return true;
			}

			if (!shortStayParams) {
				return true;
			}

			// 最長連泊期間は最短連泊期間と同じか、それよりも短くなる必要がある
			return longStayParams.minimumStayDates <= shortStayParams.maximumStayDates;
		},
		{
			message: "最長連泊期間は最短連泊期間よりも短くなるように設定してください",
			path: ["shortStayParams", "maximumStayDates"],
		},
	);

export type PlanInputZodSchemaType = z.infer<typeof planInputZodSchema>;

export type PlanInputZodParseErrorType = ZodError<PlanInputZodSchemaType>;
